import { ChevronRight, Stars } from '@mui/icons-material';
import React from 'react';
import { useConfig } from '../../../context/ConfigContext';
import { RestaurantArticlesWithDiscount } from '../../../interfaces/loyality.interface';
import './PanierDiscountCard.scss';

interface PanierDiscountCardProps {
  restaurantArticles: RestaurantArticlesWithDiscount;
  currentUserPoints: number;
  onPress: () => void;
}

const PanierDiscountCard: React.FC<PanierDiscountCardProps> = ({
  restaurantArticles,
  currentUserPoints,
  onPress,
}) => {
  const { config } = useConfig();
    const primaryColor = config?.primaryColor || '#6366F1';
  // Calculate discount message
  const remiseMessage = restaurantArticles?.cartRewardRule?.rewardDiscountType === 0
    ? `${restaurantArticles?.cartRewardRule?.rewardDiscountValue}%`
    : `${restaurantArticles?.cartRewardRule?.rewardDiscountValue?.toFixed(2)} €`;

  const requiredPoints = restaurantArticles?.cartRewardRule?.rewardTotalPtRequired || 0;
  const hasEnoughPoints = currentUserPoints >= requiredPoints;
  const missingPoints = requiredPoints - currentUserPoints;
  const progressPercentage = Math.min(100, (currentUserPoints / requiredPoints) * 100);

  // Calculate color variations
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const rgb = hexToRgb(primaryColor);
  const primaryColorLight = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`;
  const primaryColorLighter = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`;
  const primaryColorDark = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`;

  return (
    <div className="panier-discount-section">
      <div
        className="panier-discount-card"
        onClick={onPress}
        style={{
          borderColor: `${primaryColor}33`,
          '--primary-color': primaryColor,
          '--primary-color-light': primaryColorLight,
          '--primary-color-lighter': primaryColorLighter,
          '--primary-color-dark': primaryColorDark,
        } as React.CSSProperties}
      >
        <div className="card-content">
          <div className="icon-badge-container">
            <div className="icon-container">
              <Stars className="icon" />
            </div>
            <div className="points-badge">
              <span className="points-badge-text">
                {requiredPoints} pts
              </span>
            </div>
          </div>

          <div className="card-text-content">
            <h3 className="discount-title">🎉 Récompense disponible !</h3>
            <p className="discount-description">
              Économisez {remiseMessage} sur votre commande
            </p>
            <span className={`points-status ${hasEnoughPoints ? 'has-points' : 'needs-points'}`}>
              {hasEnoughPoints
                ? "✅ Vous avez assez de points !"
                : `Il vous manque ${missingPoints} points`
              }
            </span>
          </div>

          <div className="arrow-container">
            <ChevronRight className="arrow-icon" />
          </div>
        </div>

        {!hasEnoughPoints && (
          <div className="progress-container">
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <p className="progress-text">
              {currentUserPoints} / {requiredPoints} points
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PanierDiscountCard;